import { CircularProgress, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { useRecoilState, useRecoilValue } from 'recoil';
import ExportButton from '~/components/Buttons/ExportButton';
import {
    AddFilesButton,
    AddFilesIconButton,
} from '~/components/DicomUpload/AddFilesButton';
import SensitiveInfoTypography from '~/components/SensitiveInfoTypography';
import useSelectedPatient from '~/hooks/patient/useSelectedPatient';
import { useIsInSessionRoute } from '~/hooks/session/useIsInSessionRoute';
import { jobQueueState } from '~/state/atoms/jobAtoms';
import {
    exportableItemsCountState,
    exportDatasetsModalOpenState,
} from '~/state/atoms/viewerAtoms';
import SensitiveInfoToggle from '../../../../components/SensitiveInfoToggle';
import { ActionDropdown } from './ActionsDropdown';

export default function Dataset() {
    const selectedPatient = useSelectedPatient();
    const jobQueue = useRecoilValue(jobQueueState);
    const [exportStructuresModal, setExportStructureModalOpen] = useRecoilState(
        exportDatasetsModalOpenState
    );
    const inSessionRoute = useIsInSessionRoute();
    const exportableItemsCount = useRecoilValue(exportableItemsCountState);

    const allNewItems = useMemo(() => {
        return selectedPatient
            ? [
                  ...selectedPatient.doses,
                  ...selectedPatient.structureSets,
                  ...selectedPatient.images,
              ].filter(
                  (item) => item.isNew === true || item.isExported === true
              )
            : [];
    }, [
        exportableItemsCount,
        jobQueue,
        selectedPatient,
        exportStructuresModal,
    ]);

    const notExportedItems = useMemo(() => {
        return allNewItems.filter((item) => item.isExported === false);
    }, [allNewItems]);

    return (
        <Box px={2}>
            <Box>
                <Box
                    display="flex"
                    gap={1}
                    justifyContent="space-between"
                    position="relative"
                >
                    <Box overflow="hidden">
                        <SensitiveInfoTypography>
                            <Typography
                                sx={{
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                }}
                            >
                                {selectedPatient?.dataSet.PatientName ??
                                    'Patient'}
                            </Typography>
                        </SensitiveInfoTypography>
                        {selectedPatient && (
                            <SensitiveInfoTypography
                                sx={{ overflow: 'hidden' }}
                            >
                                <Typography
                                    variant="body2"
                                    fontSize={12}
                                    whiteSpace="nowrap"
                                    overflow="hidden"
                                    textOverflow="ellipsis"
                                >
                                    {selectedPatient.dataSet.PatientID}
                                </Typography>
                            </SensitiveInfoTypography>
                        )}
                    </Box>
                    <Box>
                        <Box
                            display="flex"
                            flexShrink="unset"
                            mt={-0.5}
                            gap={0.25}
                        >
                            <SensitiveInfoToggle />
                            {!inSessionRoute && <AddFilesIconButton />}
                        </Box>
                    </Box>
                </Box>
            </Box>

            {selectedPatient ? (
                <Box display="grid" gap={1} mt={1}>
                    <ActionDropdown />
                    {allNewItems.length > 0 && (
                        <ExportButton
                            newItemsCount={allNewItems.length}
                            notExportedItemsCount={notExportedItems.length}
                            onOpenExportModal={() =>
                                setExportStructureModalOpen(true)
                            }
                        />
                    )}
                </Box>
            ) : (
                <Box display="grid" gap={1}>
                    {inSessionRoute ? (
                        <Box display="flex" gap={1} width="100%">
                            <Box>
                                <CircularProgress size={12} />
                            </Box>
                            <Typography variant="body2">
                                Loading data...
                            </Typography>
                        </Box>
                    ) : (
                        <>
                            <Typography variant="body2">
                                <FormattedMessage id="noPatientDatasets" />
                            </Typography>
                            <AddFilesButton />
                        </>
                    )}
                </Box>
            )}
        </Box>
    );
}
